import React from "react";
import { Survey, SurveyReport, SurveySegment, segmentListsMatch, UnscoredSelectionStats, segmentListIsInLists } from "../../../../../../amorg-common";
import { ActionPlanListView } from "../../../../../../amorg-common/DTOs/ActionPlanListView";
import { colors } from "../../../../../../designSystem/colors/color";
import { Flex } from "../../../../../../designSystem/compositeComponents/FlexHelpers";
import { RadialPctChart } from "../../../../../../designSystem/graphs/RadialPctChart";
import { Icon } from "../../../../../../designSystem/icons/Icons";
import { P } from "../../../../../../designSystem/typography/Body";
import { PageSection } from "../../../../../../designSystem/PageSection";
import { ReadOnlySegmentPanel } from "./ReadOnlySegmentPanel";
import { ChipPanel, InfoChip } from "../../../../../../designSystem/Chip";

export function SurveyActionPlanStatusSection (props: { headerText:string, expandable?: boolean, survey: Survey, surveyReport:SurveyReport, actionPlans:ActionPlanListView[]}): JSX.Element
{

    function getSegmentsWithoutPlans (surveyReport: SurveyReport, actionPlans: ActionPlanListView[])
    {
        const segmentsWithoutPlans: SurveySegment[][] = [];
        for (const cr of surveyReport.selectionReports)
        {
            let matched = false;
            for (const ap of actionPlans)
            {
                if (ap.type === 'WPA')
                {
                    for (const selectionList of ap.segmentFilter)
                    {
                        if (segmentListsMatch(selectionList, cr.segments))
                        {
                            matched = true;
                            break;
                        }
                    }
                    if (matched)
                        break;
                }
            }
            if (!matched)
                segmentsWithoutPlans.push(cr.segments);
        }
        return segmentsWithoutPlans;
    }

    const segmentsWithoutPlans = getSegmentsWithoutPlans(props.surveyReport, props.actionPlans);

    let completionPct = 0;
    if (props.survey)
    {
        const allSegmentedAnswers = props.surveyReport.selectionReports.map(sr =>
        ({
            segments: sr.segments,
            answerCount: sr.questionIndexedSummaries.length === 0
                ? 0
                : new UnscoredSelectionStats(sr.questionIndexedSummaries[0].selectionSummary).totalSelections
        })
        );

        const segmentsWithActionplans = allSegmentedAnswers.filter(sa =>
            !segmentListIsInLists(segmentsWithoutPlans, sa.segments)
        );

        const allAnswers = allSegmentedAnswers.reduce((acc, sa) => { return acc + sa.answerCount; }, 0);
        const answersWithActionplans = segmentsWithActionplans.reduce((acc, sa) => { return acc + sa.answerCount; }, 0);

        completionPct = answersWithActionplans / allAnswers * 100;
    }

    return (
        <div>
            <PageSection
                header={{ title: props.headerText, defaultCollapsed:false }}>

                <Flex gap={10}>
                    <Flex flexDirection="column" justifyContent="flex-start">
                        <RadialPctChart pct={completionPct} />
                    </Flex>
                    <Flex flexDirection="column" gap={20} style={{flexGrow:1}}>
                        <P>
                            Spørgeskemaets besvarelser er inddelt i segmenteringer, baseret på de segmenter respondenten har valgt.<br />
                            Vi måler hvor mange procent af spørgeskemaets segmenteringer der er omfattet af en handleplan.
                        </P>
                        {props.actionPlans.length === 0 &&
                            <>
                                <P>
                                    Da der endnu ikke er oprettet handleplaner, viser grafen 0%.
                                </P>
                                <P>
                                    Når der er oprettet handleplaner, vil grafen vise hvor langt I er kommet med at afdække hele APV&apos;en med handleplaner.
                                </P>
                            </>
                        }

                        {completionPct > 0 && completionPct < 100 &&
                            <P>
                                Der mangler fortsat at blive oprettet handleplaner for en eller flere af spørgeskemaets besvarelser.
                            </P>
                        }

                        {completionPct > 99.9 &&
                            <Flex gap={10}>
                                <Icon name="actions_done" color={colors.graphs.survey.score.score5} />
                                <P>
                                    Alle spørgeskemaets besvarelser er omfattet af handleplaner!
                                </P>
                            </Flex>
                        }

                        {completionPct < 100 &&
                            <>
                                <P>
                                    Følgende segmenter er endnu ikke omfattet af en handleplan
                                </P>

                                <ChipPanel>
                                    {segmentsWithoutPlans.map((segments, index) =>
                                        <InfoChip key={index} variant="filled-light">
                                            {segments.map(s => s.value).join(' ')}
                                        </InfoChip>
                                    )}
                                </ChipPanel>
                            </>
                        }
                    </Flex>
                </Flex>
                
            </PageSection>
        </div>
    );
}